import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';

// Shared UI Library Components
import { CustomCollapse } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './counsellors-filters.scss';

// Models
import { LanguageFilter } from '../language-filter/language-filter';
import { CountryFilter } from '../country-filter/country-filter';
import { RateFilter } from '../rate-filter/rate-filter';
// import { RatingFilter } from '../rating-filter/rating-filter';
import { StatusFilter } from '../status-filter/status-filter';
import { OptionsList } from '../../options-list/options-list';

// Store entities
import { getCoreState } from '../../../../store/core/core.selectors';
import { getCounsellorsState } from '../../../../store/counsellors/counsellors.selectors';
import { clearSpecializations, fetchSpecializations } from '../../../../store/core/core.thunks';
import { saveCounsellorsFilters } from '../../../../store/counsellors/counsellors.thunks';

// Models
import { ICounsellorsFilters, ICounsellorsFiltersForm } from '../../../../interfaces/counsellors.interfaces';
import { TValueOf } from '../../../../types/core.types';

type TCounsellorsFiltersProps = {
  openFilters?: boolean;
};

const fieldNames: Array<keyof ICounsellorsFilters> = [
  'languages',
  'countries',
  // TODO next release
  // 'review_rating',
  'status',
  'rate_from',
  'rate_to',
  'specialization',
  'specialities',
];

export const CounsellorsFilters: FC<TCounsellorsFiltersProps> = (
  { openFilters }: TCounsellorsFiltersProps
) => {
  const [form] = Form.useForm();

  const {
    languages,
    countries,
    speciality,
    specializations
  } = useSelector(getCoreState);

  const { filters: counsellorFilters } = useSelector(getCounsellorsState);

  const dispatch = useDispatch<any>();

  const filtersChange = (filters: ICounsellorsFilters): void => {
    dispatch(saveCounsellorsFilters({ ...counsellorFilters, ...filters }));
  }

  const getFormFieldValues = (): { name: string, value: TValueOf<ICounsellorsFilters> }[] => {
    const fields: {
      [key in keyof ICounsellorsFilters]: {
        name: string,
        value: TValueOf<ICounsellorsFilters>
      }
    } = fieldNames.reduce((acc, fieldName) => ({
      ...acc,
      [fieldName]: {
        name: fieldName,
        value: counsellorFilters[fieldName],
      }
    }), {});

    return Object.values(fields);
  }

  const onChangeTabFilters = (_: string | string[]) => {
    // Change tab event handler
  };

  const onFormValuesChange = (
    _: { [key in keyof ICounsellorsFiltersForm]: TValueOf<ICounsellorsFiltersForm> },
    { specialization, specialities, ...allValues }: ICounsellorsFiltersForm
  ): void => {
    filtersChange({ ...counsellorFilters, ...allValues });
  }

  const onSelectSpecialty = (specialities: number[]) => {
    const isNew = counsellorFilters.specialities?.every(spec => !specialities.includes(spec));

    const newFilters = {
      ...counsellorFilters,
      specialities,
      ...(isNew && { specialization: [] })
    };

    if (!specialities.length) {
      dispatch(clearSpecializations());
      newFilters.specialization = [];
    }

    filtersChange(newFilters);

    if (!specialities.length) return;

    dispatch(fetchSpecializations({
      speciality: specialities.map((id) => ({
        name: `${ id }`,
        id
      }))
    }));
  };

  const onSelectSpecialization = (specialization: number[]) => {
    filtersChange({ ...counsellorFilters, specialization });
  };

  useEffect(() => {
    form.setFields(getFormFieldValues());
  }, [counsellorFilters]);

  return (
    <Form className={ `counsellors-filters ${ openFilters ? 'open-filters' : '' }` } form={ form }
          onValuesChange={ onFormValuesChange }>
      <CustomCollapse
        accordion={ true }
        items={ [
          {
            key: 'speciality',
            label: 'Specialty',
            children: <OptionsList selectedList={ counsellorFilters.specialities ?? [] }
                                   options={ speciality.list }
                                   onSelectItem={ onSelectSpecialty }/>,
          },
          {
            key: 'specialisation',
            label: 'Specialisation',
            children: <OptionsList selectedList={ counsellorFilters.specialization ?? [] }
                                   options={ specializations.list }
                                   onSelectItem={ onSelectSpecialization }/>,
            headerClass: !counsellorFilters.specialities?.length ? 'hidden-panel' : ''
          },
          {
            key: 'languages',
            label: 'Language',
            children: <LanguageFilter languages={ languages.list }/>,
          },
          {
            key: 'countries',
            label: 'Country',
            children: <CountryFilter countries={ countries.list }/>,
          },
          {
            key: 'rates',
            label: 'Rate',
            children: <RateFilter/>,
          },
          // TODO next release
          // {
          //   key: 'review_rating',
          //   label: 'Rating',
          //   children: <RatingFilter/>,
          // },
          {
            key: 'status',
            label: 'Status',
            children: <StatusFilter/>,
          },
        ] }
        onChangeTab={ onChangeTabFilters }/>
    </Form>
  );
}
